import { PopoverForOverflownText, TableColumn, TableRow } from '@lifebit-ai/lds'
import { ResultsState, ValidationStatuses } from 'enums'
import React from 'react'

import { AssigneeColumnContent } from 'modules/requests/components/AssigneeColumnContent'
import { ExternalRequestTag } from 'modules/requests/components/ExternalRequestTag'
import { LabelsColumnContent } from 'modules/requests/components/LabelsColumnContent'
import ResultState from 'modules/requests/components/ResultState'
import type { Result } from 'services/results/types'
import { formatDate } from 'utils/date'

import { getExportRequestName, getRequestTypeDisplay, isDataAccessRequest, isExternalDataAccessRequest } from '../utils'
import { StyledHeaderName } from './ExportAndAccessRequestsTable.styles'
import * as ExportRequestTableStyles from './RequestsTable.styles'

export const TEST_ID_EXPORT_REQUEST_DATASET_ICON = 'export-request-dataset-icon'
export const TEST_ID_EXPORT_REQUEST_FILE_ICON = 'export-request-file-icon'

const getValidationStatusDisplay = (validationStatus?: ValidationStatuses): string => {
  if (!validationStatus) return '-'

  switch (validationStatus) {
    case ValidationStatuses.Verified:
      return 'Verified'
    case ValidationStatuses.NotChecked:
      return 'Not checked'
    case ValidationStatuses.AutoCheckInProgress:
      return 'Auto-check in progress'
    case ValidationStatuses.AutoCheckPassed:
      return 'Auto-check passed'
    case ValidationStatuses.AutoCheckFailed:
      return 'Auto-check failed'
    default:
      return validationStatus
  }
}

export const getColumnsForFilesAndFolders = (): TableColumn<Result>[] => [
  {
    title: 'Status',
    render: (row: TableRow<Result>) => {
      return <ResultState state={row.original} />
    },
    id: 'status',
    width: 160
  },
  {
    title: 'Dataset / File',
    id: 'name',
    render: (row: TableRow<Result>) => {
      const isDataset = isDataAccessRequest(row.original)
      const displayName = getExportRequestName(row.original)

      return (
        <ExportRequestTableStyles.DatasetFileCellContainer>
          {isDataset ? (
            <ExportRequestTableStyles.DatasetIcon aria-hidden data-testid={TEST_ID_EXPORT_REQUEST_DATASET_ICON} />
          ) : (
            <ExportRequestTableStyles.FileIcon aria-hidden data-testid={TEST_ID_EXPORT_REQUEST_FILE_ICON} />
          )}
          <ExportRequestTableStyles.DatasetFileCellText>
            <PopoverForOverflownText title={displayName}>{displayName}</PopoverForOverflownText>
          </ExportRequestTableStyles.DatasetFileCellText>
          {isExternalDataAccessRequest(row.original) && <ExternalRequestTag />}
        </ExportRequestTableStyles.DatasetFileCellContainer>
      )
    },
    width: 250
  },
  {
    title: 'Labels',
    id: 'labelIds',
    render: (row: TableRow<Result>) => {
      return <LabelsColumnContent labels={row.original.labels} />
    }
  },
  {
    title: 'Requested by',
    render: (row: TableRow<Result>) => {
      const { requestingUser } = row.original
      return (
        <PopoverForOverflownText title={requestingUser.email.toLowerCase()}>
          {requestingUser.email.toLowerCase()}
        </PopoverForOverflownText>
      )
    },
    id: 'requestedBy',
    width: 250
  },
  {
    title: 'Request type',
    id: 'requestType',
    render: (row: TableRow<Result>) => {
      const requestTypeDisplay = getRequestTypeDisplay(row.original.requestType)
      return <PopoverForOverflownText title={requestTypeDisplay}>{requestTypeDisplay}</PopoverForOverflownText>
    },
    width: 170
  },
  {
    title: 'Request date',
    render: (row: TableRow<Result>) => <StyledHeaderName>{formatDate(row.original.createdAt)}</StyledHeaderName>,
    id: 'requestDate',
    width: 200
  },
  {
    title: 'Assignee',
    id: 'assignedTo',
    canSort: true,
    render: (row: TableRow<Result>) => <AssigneeColumnContent assignedTo={row.original.assignedTo} />
  },
  {
    title: 'Decided by',
    id: 'decidedBy',
    render: (row: TableRow<Result>) => {
      const { isAutomaticApproved, verifiedBy, status } = row.original
      // Show "-" for pending requests
      if (status === ResultsState.PendingVerification) {
        return <PopoverForOverflownText title="-">-</PopoverForOverflownText>
      }
      const decidedBy = isAutomaticApproved ? 'Automation' : verifiedBy?.email || '-'
      return <PopoverForOverflownText title={decidedBy}>{decidedBy}</PopoverForOverflownText>
    }
  },
  {
    title: 'Decision date',
    id: 'decisionDate',
    render: (row: TableRow<Result>) => {
      const { status, verifiedAt } = row.original
      // Show "-" for pending requests
      if (status === ResultsState.PendingVerification) {
        return <StyledHeaderName>-</StyledHeaderName>
      }
      // Show formatted date for non-pending requests with verifiedAt
      const displayDate = verifiedAt ? formatDate(verifiedAt) : '-'
      return <StyledHeaderName>{displayDate}</StyledHeaderName>
    },
    width: 200
  },
  {
    title: 'Validation status',
    id: 'validationStatus',
    render: (row: TableRow<Result>) => {
      const displayStatus = getValidationStatusDisplay(row.original.validationStatus)
      return <PopoverForOverflownText title={displayStatus}>{displayStatus}</PopoverForOverflownText>
    },
    width: 200
  },

  {
    title: 'Workspace name',
    id: 'workspaceName',
    render: (row: TableRow<Result>) => {
      const { workspaceName } = row.original
      return <PopoverForOverflownText title={workspaceName}>{workspaceName}</PopoverForOverflownText>
    },
    width: 200
  }
]

export const getColumnsForAnalysis = (): TableColumn<Result>[] => [
  {
    title: 'Status',
    render: (row: TableRow<Result>) => <ResultState state={row.original} />,
    id: 'status',
    width: 120
  },
  {
    title: 'Analysis name',
    render: (row: TableRow<Result>) => {
      const { description } = row.original
      return <PopoverForOverflownText title={description}>{description}</PopoverForOverflownText>
    },
    id: 'name'
  },
  {
    title: 'Requested by',
    render: (row: TableRow<Result>) => {
      const { requestingUser } = row.original
      return (
        <PopoverForOverflownText title={requestingUser.email.toLowerCase()}>
          {requestingUser.email.toLowerCase()}
        </PopoverForOverflownText>
      )
    },
    id: 'requestedBy'
  },
  {
    title: 'Request date',
    render: (row: TableRow<Result>) => <StyledHeaderName>{formatDate(row.original.createdAt)}</StyledHeaderName>,
    id: 'requestDate'
  },
  {
    title: 'Decided by',
    id: 'decidedBy',
    render: (row: TableRow<Result>) => {
      const { isAutomaticApproved, verifiedBy, status } = row.original
      // Show "-" for pending requests
      if (status === ResultsState.PendingVerification) {
        return <PopoverForOverflownText title="-">-</PopoverForOverflownText>
      }
      const decidedBy = isAutomaticApproved ? 'Automation' : verifiedBy?.email || '-'
      return <PopoverForOverflownText title={decidedBy}>{decidedBy}</PopoverForOverflownText>
    },
    width: 140
  },
  {
    title: 'Decision date',
    id: 'decisionDate',
    render: (row: TableRow<Result>) => {
      const { status, verifiedAt } = row.original
      // Show "-" for pending requests
      if (status === ResultsState.PendingVerification) {
        return <StyledHeaderName>-</StyledHeaderName>
      }
      // Show formatted date for non-pending requests with verifiedAt
      const displayDate = verifiedAt ? formatDate(verifiedAt) : '-'
      return <StyledHeaderName>{displayDate}</StyledHeaderName>
    }
  },
  {
    title: 'Validation status',
    id: 'validationStatus',
    render: (row: TableRow<Result>) => {
      const displayStatus = getValidationStatusDisplay(row.original.validationStatus)
      return <PopoverForOverflownText title={displayStatus}>{displayStatus}</PopoverForOverflownText>
    },
    width: 160
  },
  {
    title: 'Workspace name',
    render: (row: TableRow<Result>) => {
      const { workspaceName } = row.original
      return <PopoverForOverflownText title={workspaceName}>{workspaceName}</PopoverForOverflownText>
    },
    id: 'workspaceName',
    width: 170
  }
]
